import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { BadgeProvider, useBadges, BadgeCount } from '../../hooks/useBadges';
import { 
  Building2, 
  Users, 
  ClipboardList, 
  TrendingUp, 
  Calendar,
  CalendarDays,
  FileText,
  Home,
  BarChart3,
  MessageSquare,
  Inbox,
  FolderOpen,
  HardHat,
  Briefcase,
  UserCheck,
  Archive,
  Bell,
  StickyNote,
  Wrench
} from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import PanelHeader from '../../components/PanelHeader';

// Ana BranchPanel içeriği
const BranchPanelContent = () => {
  const location = useLocation();
  const { getBadgeCount, markSeen } = useBadges();
  const [unreadCounts, setUnreadCounts] = useState({});
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  // unreadReportsCount state removed - using getBadgeFromSystem('reports') instead
  const [notificationCounts, setNotificationCounts] = useState({});
  const [badgeCounts, setBadgeCounts] = useState({});
  const [jobsNotificationCount, setJobsNotificationCount] = useState(0);
  const [pendingRequestsCount, setPendingRequestsCount] = useState(0);
  const [unseenRequestsCount, setUnseenRequestsCount] = useState(0); // YENİ talepler için
  const [unseenJobsCount, setUnseenJobsCount] = useState(0); // YENİ işler için
  const [subcontractorNewCount, setSubcontractorNewCount] = useState(0); // Taşeron yeni kayıt sayısı
  const [newFilesCount, setNewFilesCount] = useState(0); // Dosyalar yeni belge sayısı
  const [newWallCount, setNewWallCount] = useState(0); // Şube Duvarı yeni paylaşım/yorum sayısı
  const [newServicesCount, setNewServicesCount] = useState(0); // Alınan Hizmetler yeni/güncellenen sayısı
  const [unseenPersonnelCount, setUnseenPersonnelCount] = useState(0); // Personel Talepleri okunmamış avans sayısı

  const fetchUnreadCounts = useCallback(async () => {
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/unread-counts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUnreadCounts(data);
        // Badge system now used for reports count
      }
    } catch (error) {
      console.error('Okunmamış sayıları alınamadı:', error);
    }
  }, []);

  const fetchNotificationCounts = useCallback(async () => {
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/counts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setNotificationCounts(data);
      }
    } catch (error) {
      console.error('Bildirim sayıları alınamadı:', error);
    }
  }, []);

  // Badge counts (seen-based) - şubedeki yeni öğeler
  const fetchBadgeCounts = useCallback(async () => {
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/badges/counts?panel=branch`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setBadgeCounts(data);
      }
    } catch (error) {
      console.error('Badge sayıları alınamadı:', error);
    }
  }, []);

  const fetchJobsNotificationCount = useCallback(async () => {
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/branch/jobs/notification-count`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setJobsNotificationCount(data.count || 0);
      }
    } catch (error) {
      console.error('İşler bildirim sayısı alınamadı:', error);
    }
  }, []);

  const fetchPendingRequestsCount = useCallback(async () => {
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/branch/requests/pending-count`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPendingRequestsCount(data.count || 0);
      }
    } catch (error) {
      console.error('Bekleyen talep sayısı alınamadı:', error);
    }
  }, []);

  // Görülmemiş (YENİ) talep sayısını çek
  const fetchUnseenRequestsCount = useCallback(async () => {
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/branch/requests/unseen-count`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUnseenRequestsCount(data.unseen_count || 0);
      }
    } catch (error) {
      console.error('Görülmemiş talep sayısı alınamadı:', error);
    }
  }, []);

  // Personel Talepleri okunmamış avans sayısını çek
  const fetchUnseenPersonnelCount = useCallback(async () => {
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/branch/personnel-requests/unseen-count`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUnseenPersonnelCount(data.unseen_count || 0);
      }
    } catch (error) {
      console.error('Okunmamış personel talebi sayısı alınamadı:', error);
    }
  }, []);

  // Görülmemiş (YENİ) iş sayısını çek
  const fetchUnseenJobsCount = useCallback(async () => {    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/branch/jobs/unseen-count`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUnseenJobsCount(data.unseen_count || 0);
      }
    } catch (error) {
      console.error('Görülmemiş iş sayısı alınamadı:', error);
    }
  }, []);

  // Taşeron yeni kayıt sayısını çek
  const fetchSubcontractorNewCount = useCallback(async () => {
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/branch/subcontractors/new-counts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSubcontractorNewCount(data.total_new || 0);
      }
    } catch (error) {
      console.error('Taşeron yeni sayısı alınamadı:', error);
    }
  }, []);

  // Dosyalar yeni belge sayısını çek
  const fetchNewFilesCount = useCallback(async () => {
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/branch/files/new-count`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setNewFilesCount(data.new_count || 0);
      }
    } catch (error) {
      console.error('Dosyalar yeni belge sayısı alınamadı:', error);
    }
  }, []);

  // Şube Duvarı yeni paylaşım/yorum sayısını çek
  const fetchNewWallCount = useCallback(async () => {
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/wall/new-count`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setNewWallCount(data.total_new || 0);
      }
    } catch (error) {
      console.error('Şube Duvarı yeni sayısı alınamadı:', error);
    }
  }, []);

  // Alınan Hizmetler yeni/güncellenen sayısını çek
  const fetchNewServicesCount = useCallback(async () => {
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/branch/services/new-count`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setNewServicesCount(data.total_new || 0);
      }
    } catch (error) {
      console.error('Alınan Hizmetler yeni sayısı alınamadı:', error);
    }
  }, []);

  const fetchUnreadMessageCount = useCallback(async () => {
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/work/messages/unread-count`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUnreadMessageCount(data.unread_count || 0);
      }
    } catch (error) {
      console.error('Okunmamış mesaj sayısı alınamadı:', error);
    }
  }, []);

  useEffect(() => {
    fetchUnreadCounts();
    fetchUnreadMessageCount();
    fetchNotificationCounts();
    fetchBadgeCounts();
    fetchJobsNotificationCount();
    fetchPendingRequestsCount();
    fetchUnseenRequestsCount();
    fetchUnseenJobsCount();
    fetchSubcontractorNewCount();
    fetchNewFilesCount();
    fetchNewWallCount();
    fetchNewServicesCount();
    fetchUnseenPersonnelCount();
    // Her 5 saniyede bir güncelle - anlık bildirimler için
    const interval = setInterval(() => {
      fetchUnreadCounts();
      fetchUnreadMessageCount();
      fetchNotificationCounts();
      fetchBadgeCounts();
      fetchJobsNotificationCount();
      fetchPendingRequestsCount();
      fetchUnseenRequestsCount();
      fetchUnseenJobsCount();
      fetchSubcontractorNewCount();
      fetchNewFilesCount();
      fetchNewWallCount();
      fetchNewServicesCount();
      fetchUnseenPersonnelCount();
    }, 5000);
    
    // Custom event dinle - mesaj okunduğunda güncelle
    const handleReadUpdate = () => {
      fetchUnreadCounts();
      fetchUnreadMessageCount();
      fetchNotificationCounts();
      fetchBadgeCounts();
      fetchJobsNotificationCount();
      fetchPendingRequestsCount();
      fetchUnseenRequestsCount();
      fetchUnseenJobsCount();
      fetchSubcontractorNewCount();
      fetchNewServicesCount();
      fetchUnseenPersonnelCount();
    };
    window.addEventListener('unread-counts-updated', handleReadUpdate);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('unread-counts-updated', handleReadUpdate);
    };
  }, [fetchUnreadCounts, fetchUnreadMessageCount, fetchNotificationCounts, fetchJobsNotificationCount, fetchPendingRequestsCount, fetchUnseenRequestsCount, fetchUnseenJobsCount, fetchNewServicesCount]);

  // Sayfa değiştiğinde sayıları güncelle
  useEffect(() => {
    fetchUnreadCounts();
    fetchUnreadMessageCount();
    fetchNotificationCounts();
    fetchJobsNotificationCount();
    fetchPendingRequestsCount();
    fetchUnseenRequestsCount();
    fetchUnseenJobsCount();
    fetchUnseenPersonnelCount();
  }, [location.pathname, fetchUnreadCounts, fetchUnreadMessageCount, fetchNotificationCounts, fetchJobsNotificationCount, fetchPendingRequestsCount, fetchUnseenRequestsCount, fetchUnseenJobsCount]);

  // Mark badge as seen when menu clicked
  const markBadgeSeen = useCallback(async (menuKey) => {
    try {
      const token = localStorage.getItem('fortis_token');
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/badges/mark-seen`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          panel: 'branch',
          menu: menuKey,
          tab: '',
          sub_tab: ''
        })
      });
      // Refresh badge counts
      fetchBadgeCounts();
      fetchNotificationCounts();
    } catch (error) {
      console.error('Badge seen işaretlenemedi:', error);
    }
  }, [fetchBadgeCounts, fetchNotificationCounts]);

  // Helper: Get badge count from badge system only (not notifications)
  const getBadgeFromSystem = (badgeKey) => {
    return badgeCounts[badgeKey]?.total || 0;
  };

  const menuItems = [
    { path: '/branch/dashboard', icon: Home, label: 'Ana Sayfa', menuKey: 'dashboard', badgeKey: null },
    { path: '/branch/employees', icon: Users, label: 'Şube Çalışanları', menuKey: 'employees', badgeKey: 'employees', getBadge: () => getBadgeFromSystem('employees') },
    { path: '/branch/attendance', icon: Calendar, label: 'Mesai Takibi', menuKey: 'attendance', badgeKey: 'attendance', getBadge: () => getBadgeFromSystem('attendance') },
    { path: '/branch/assignments', icon: TrendingUp, label: 'İş Programı', menuKey: 'schedule', badgeKey: 'assignments', getBadge: () => getBadgeFromSystem('assignments') },
    { path: '/branch/calendar', icon: CalendarDays, label: 'Takvim', menuKey: 'calendar', badgeKey: 'calendar', getBadge: () => getBadgeFromSystem('calendar'), isUnseen: getBadgeFromSystem('calendar') > 0 },
    { path: '/branch/reports', icon: FileText, label: 'Raporlar', menuKey: 'reports', badgeKey: 'reports', getBadge: () => getBadgeFromSystem('reports'), isUnseen: getBadgeFromSystem('reports') > 0 },
    { path: '/branch/subcontractors', icon: HardHat, label: 'Taşeronlar', menuKey: 'subcontractors', badgeKey: 'subcontractors', getBadge: () => subcontractorNewCount, isUnseen: subcontractorNewCount > 0 },
    { path: '/branch/services', icon: Wrench, label: 'Alınan Hizmetler', menuKey: 'services', badgeKey: 'services', getBadge: () => newServicesCount, isUnseen: newServicesCount > 0 },
    { path: '/branch/jobs', icon: Briefcase, label: 'İşler', menuKey: 'jobs', badgeKey: 'jobs', getBadge: () => unseenJobsCount + unseenRequestsCount, isUnseen: true },
    { path: '/branch/sube-bilgileri', icon: FolderOpen, label: 'Şube Bilgileri', menuKey: 'branch_info', badgeKey: 'branch_info', getBadge: () => getBadgeFromSystem('branch_info') },
    { path: '/branch/iletisim', icon: Inbox, label: 'İletişim', menuKey: 'communication', badgeKey: 'communication', getBadge: () => getBadgeFromSystem('communication') },
    { path: '/branch/files', icon: Archive, label: 'Dosyalar', menuKey: 'files', badgeKey: null, getBadge: () => newFilesCount, isUnseen: newFilesCount > 0 },
    { path: '/branch/wall', icon: StickyNote, label: 'Şube Duvarı', menuKey: 'wall', badgeKey: null, getBadge: () => newWallCount, isUnseen: newWallCount > 0 },
    { path: '/branch/personnel-requests', icon: ClipboardList, label: 'Personel Talepleri', menuKey: 'personnel_requests', badgeKey: null, getBadge: () => unseenPersonnelCount, isUnseen: unseenPersonnelCount > 0 },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]" data-testid="branch-panel">
      {/* Header */}
      <PanelHeader
        title="Şube Yönetim Paneli"
        titleIcon={Building2}
        titleColor="text-amber-600"
      />

      {/* Add pt-16 to account for fixed header height (h-16 = 4rem) */}
      <div className="flex pt-16">
        {/* Sidebar */}
        <aside className="w-64 min-h-[calc(100vh-4rem)] border-r border-[hsl(var(--border))] bg-white p-4">
          <nav className="space-y-2">
            {menuItems.map((item) => {
              const Icon = item.icon;
              // Badge sayısını belirle - getBadge fonksiyonu varsa onu kullan
              let badgeCount = 0;
              
              if (item.getBadge) {
                badgeCount = item.getBadge();
              } else {
                // Eski sistem - yedek olarak
                const newBadgeCount = getBadgeCount(item.menuKey);
                if (newBadgeCount > 0) {
                  badgeCount = newBadgeCount;
                } else if (item.badgeKey && notificationCounts[item.badgeKey]) {
                  badgeCount = notificationCounts[item.badgeKey];
                } else if (item.badgeKey && unreadCounts[item.badgeKey]) {
                  badgeCount = unreadCounts[item.badgeKey];
                }
              }
              
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => {
                    item.menuKey && markSeen(item.menuKey);
                    item.menuKey && markBadgeSeen(item.menuKey);
                  }}
                  className={`flex items-center gap-3 px-4 py-3 rounded-md transition-colors ${
                    isActive(item.path)
                      ? 'bg-amber-600 text-white'
                      : 'hover:bg-amber-50 text-gray-700'
                  }`}
                  data-testid={`menu-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                >
                  <Icon className="h-5 w-5" />
                  <span className="font-medium">{item.label}</span>
                  {badgeCount > 0 && (
                    <Badge className={`ml-auto ${
                      isActive(item.path) ? 'bg-white text-amber-600' : 'bg-red-500 text-white'
                    } ${item.isUnseen ? 'animate-pulse' : ''}`}>
                      {badgeCount}
                    </Badge>
                  )}
                </Link>
              );
            })}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

// BadgeProvider ile sarılmış ana bileşen
const BranchPanel = () => {
  return (
    <BadgeProvider panel="branch">
      <BranchPanelContent />
    </BadgeProvider>
  );
};

export default BranchPanel;
